import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { fetchOrderDetail } from "../../redux/ordersSlice";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaInfoCircle,
  FaFileInvoice,
} from "react-icons/fa";
import formatPrice from "../../utils/formatPrice";

const OrderDetail = () => {
  const navigate = useNavigate();
  const { orderId } = useParams(); // Get order ID from route
  const dispatch = useDispatch();
  const { detail, detailStatus } = useSelector((state) => state.orders);
  const precision = sessionStorage.getItem("prec");

  useEffect(() => {
    dispatch(fetchOrderDetail(orderId));
  }, [dispatch, orderId]);

  if (detailStatus === "loading") {
    return <div className="text-center py-10">Loading order details...</div>;
  }

  if (detailStatus === "failed") throw new Error("Could not get order detail");

  if (!detail) {
    return <div className="text-center py-10">No order found.</div>;
  }

  return (
    <div className="min-h-screen bg-gray-100 py-10 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto bg-white shadow-lg rounded-lg p-6">
        {/* Order Info */}
        <div className="mb-8">
          <h2 className="text-xl font-bold mb-4 border-b pb-2 flex flex-col sm:flex-row sm:space-x-4">
            <span>Order Information</span>
            <div className="flex flex-col sm:flex-row sm:space-x-4 text-base items-start sm:items-end font-medium">
              <div>
                <p>{detail?.doc_num}</p>
              </div>
              <div>
                <p>{detail?.date}</p>
              </div>
              <span
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                  detail?.order_status === "Delivered"
                    ? "bg-green-100 text-green-700"
                    : detail?.order_status === "Pending" ||
                        detail?.order_status === "To Invoice"
                      ? "bg-yellow-100 text-yellow-700"
                      : "bg-red-100 text-red-700"
                }`}
              >
                {detail?.order_status === "Delivered" && <FaCheckCircle />}
                {detail?.order_status === "Pending" && <FaInfoCircle />}
                {detail?.order_status === "Closed" && <FaTimesCircle />}
                {detail?.order_status === "To Invoice" && <FaFileInvoice />}
                {detail?.order_status}
              </span>
            </div>
          </h2>
        </div>

        {/* Order Items */}
        <div>
          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-200 text-sm">
              <thead>
                <tr className="bg-gray-200 text-gray-600 text-sm uppercase">
                  <th className="py-2 px-4 text-left">Name</th>
                  <th className="py-2 px-4 text-left">ID</th>
                  <th className="py-2 px-4 text-center">Qty</th>
                  <th className="py-2 px-4 text-center">Unit</th>
                  <th className="py-2 px-4 text-right">Unit Price</th>
                  <th className="py-2 px-4 text-right">Discount %</th>
                  <th className="py-2 px-4 text-right">VAT %</th>
                  <th className="py-2 px-4 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {detail?.detail?.map((item, index) => (
                  <tr
                    key={index}
                    className={`border-b cursor-pointer ${index % 2 === 0 ? "bg-gray-50" : "bg-white"} hover:bg-gray-300`}
                    onClick={() => navigate("/product/" + item?.product_id)}
                  >
                    <td className="py-2 px-4 break-words relative">
                      <span className="relative underline-animate hover:text-blue-700">
                        {item?.product_name}
                      </span>
                    </td>
                    <td className="py-2 px-4">{item?.product_id}</td>
                    <td className="py-2 px-4 text-center">
                      {Number(item?.qty)?.toFixed(precision)}
                    </td>
                    <td className="py-2 px-4 text-center">{item?.unit}</td>
                    <td className="py-2 px-4 text-right">
                      {formatPrice(Number(item?.unitprice).toFixed(precision))}
                    </td>
                    <td className="py-2 px-4 text-right">{item?.disc_pc}</td>
                    <td className="py-2 px-4 text-right">{item?.vat_pc}</td>
                    <td className="py-2 px-4 text-right">
                      {formatPrice(Number(item?.total)?.toFixed(precision))}
                    </td>
                  </tr>
                ))}

                {/* Totals */}
                <tr className="bg-gray-200 font-bold">
                  <td className="py-2 px-4" colSpan="4">
                    Total
                  </td>
                  <td className="py-2 px-4 text-right">
                    {formatPrice(
                      Number(detail?.order_amount)?.toFixed(precision),
                    )}
                  </td>
                  <td className="py-2 px-4 text-right">
                    {formatPrice(
                      Number(detail?.order_discount)?.toFixed(precision),
                    )}
                  </td>
                  <td className="py-2 px-4 text-right">
                    {formatPrice(Number(detail?.order_vat).toFixed(precision))}
                  </td>
                  <td className="py-2 px-4 text-right">
                    {formatPrice(Number(detail?.total)?.toFixed(precision))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetail;
